import { HttpClient, HttpParams } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { AuthService } from '../../../../core/auth/services/auth.service';
import { API_BASE_URL } from '../../../../core/services/api.config';
import { CaseCardComponent } from '../../../../shared/components/case-card/case-card.component';
import { CaseCardData } from '../../../../shared/models/case-card.model';

type CaseStatusFilter = 'open' | 'closed' | 'all';

interface HomeFilterState {
  status: CaseStatusFilter;
  partnerOrganizationId: number | null;
  search: string;
  toBeClosedOnly: boolean;
}

interface HomePartnerOption {
  id: number;
  name: string;
}

interface HomeCaseItem {
  id: number;
  caseUniqueId?: string;
  clientId: number;
  status: 'open' | 'closed';
  priority?: 'low' | 'medium' | 'high';
  referralSource?: 'hospital' | 'school' | 'community_based_organization' | 'self' | 'other';
  referralSourceOther?: string | null;
  referralDate?: string;
  serviceName: string;
  partnerName: string;
  openedDate: string;
  closeDate: string | null;
  totalHours: number;
  totalUnits: number;
  unitLabel: string;
}

interface HomeClientCaseGroup {
  clientId: number;
  clientName: string;
  clientStatus: 'open' | 'closed';
  birthdate: string | null;
  initialContactReason: string;
  cases: HomeCaseItem[];
}

interface HomeStats {
  clientsServed: number;
  openCases: number;
  toBeClosedCount: number;
  violentIncidentCount: number;
  topService: { serviceName: string; caseCount: number } | null;
  zipCodesTop5: Array<{ zipCode: string; caseCount: number }>;
  ageRangeRace: Array<{ ageRange: string; races: Array<{ race: string; caseCount: number }> }>;
}

interface HomeFeedResponse {
  groups: HomeClientCaseGroup[];
  totalClients: number;
  totalCases: number;
  stats: HomeStats;
}

interface PartnersResponse {
  data: HomePartnerOption[];
  count: number;
}

const HOME_FILTER_COOKIE_NAME = 'ovp.home.filters';

@Component({
  selector: 'app-home',
  imports: [CommonModule, FormsModule, CaseCardComponent, RouterLink],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly apiBaseUrl = inject(API_BASE_URL);

  private initialized = false;

  readonly loading = signal(false);
  readonly loadingPartners = signal(false);
  readonly errorMessage = signal<string | null>(null);

  readonly groups = signal<HomeClientCaseGroup[]>([]);
  readonly stats = signal<HomeStats | null>(null);
  readonly totalCases = signal(0);
  readonly totalClients = signal(0);
  readonly partners = signal<HomePartnerOption[]>([]);

  readonly filters = signal<HomeFilterState>({
    status: 'open',
    partnerOrganizationId: null,
    search: '',
    toBeClosedOnly: false,
  });

  readonly draftFilters = signal<HomeFilterState>(this.filters());

  readonly role = computed(() => this.authService.role());
  readonly currentUser = this.authService.user;
  readonly isDekalbUser = computed(() => this.role() === 'dekalb');
  readonly canUseToBeClosed = computed(
    () => this.role() === 'dekalb' || this.role() === 'admin',
  );

  readonly groupedCaseCards = computed(() =>
    this.groups().map((group) => ({
      ...group,
      caseCards: group.cases.map((item): CaseCardData => ({
        id: item.id,
        caseUniqueId: item.caseUniqueId ?? String(item.id),
        clientId: item.clientId,
        clientName: group.clientName,
        priority: item.priority ?? 'medium',
        referralSource: item.referralSource ?? 'other',
        referralSourceOther: item.referralSourceOther ?? null,
        referralDate: item.referralDate ?? item.openedDate,
        openedDate: item.openedDate,
        serviceName: item.serviceName,
        partnerName: item.partnerName,
        closeDate: item.closeDate,
        totalHours: item.totalHours,
        totalUnits: item.totalUnits,
        unitLabel: item.unitLabel,
        status: item.status,
      })),
    })),
  );

  readonly maxZipCount = computed(() => {
    const values = this.stats()?.zipCodesTop5.map((item) => item.caseCount) ?? [];
    return values.length ? Math.max(...values) : 1;
  });

  readonly toBeClosedCount = computed(() => this.stats()?.toBeClosedCount ?? 0);

  constructor() {
    effect(() => {
      const user = this.currentUser();
      if (!user || this.initialized) {
        return;
      }

      this.initialized = true;
      const partnerOrgId = user.partnerOrganizationId ?? null;
      const initialFilters = this.readFiltersFromCookie(user.role, partnerOrgId);
      this.filters.set(initialFilters);
      this.draftFilters.set(initialFilters);
      this.fetchPartners();
      this.fetchHomeFeed();
    });
  }

  setStatusFilter(status: CaseStatusFilter): void {
    const user = this.currentUser();
    if (!user) {
      return;
    }

    const nextFilters = this.normalizeFilters(
      { ...this.filters(), status, toBeClosedOnly: false },
      user.role,
      user.partnerOrganizationId ?? null,
    );
    this.filters.set(nextFilters);
    this.draftFilters.set(nextFilters);
    this.writeFiltersCookie(nextFilters);
    this.fetchHomeFeed();
  }

  setToBeClosedFilter(): void {
    if (!this.canUseToBeClosed()) {
      return;
    }

    const user = this.currentUser();
    if (!user) {
      return;
    }

    const currentlyEnabled = this.filters().toBeClosedOnly;
    const nextFilters = this.normalizeFilters(
      {
        ...this.filters(),
        toBeClosedOnly: !currentlyEnabled,
        status: currentlyEnabled ? 'open' : 'all',
      },
      user.role,
      user.partnerOrganizationId ?? null,
    );
    this.filters.set(nextFilters);
    this.draftFilters.set(nextFilters);
    this.writeFiltersCookie(nextFilters);
    this.fetchHomeFeed();
  }

  runClientSearch(): void {
    const user = this.currentUser();
    if (!user) {
      return;
    }

    const nextFilters = this.normalizeFilters(
      {
        ...this.filters(),
        search: this.draftFilters().search,
        partnerOrganizationId: this.draftFilters().partnerOrganizationId,
      },
      user.role,
      user.partnerOrganizationId ?? null,
    );

    this.filters.set(nextFilters);
    this.draftFilters.set(nextFilters);
    this.writeFiltersCookie(nextFilters);
    this.fetchHomeFeed();
  }

  zipBarWidth(count: number): number {
    const max = this.maxZipCount();
    if (!max) {
      return 0;
    }
    return Math.max(8, Math.round((count / max) * 100));
  }

  raceBarWidth(raceCount: number, races: Array<{ race: string; caseCount: number }>): number {
    const max = races.length ? Math.max(...races.map((item) => item.caseCount)) : 1;
    if (!max) {
      return 0;
    }
    return Math.max(10, Math.round((raceCount / max) * 100));
  }

  trackGroupByClientId(_index: number, group: HomeClientCaseGroup): number {
    return group.clientId;
  }

  setDraftSearch(value: string): void {
    this.draftFilters.set({ ...this.draftFilters(), search: value });
  }

  setDraftPartnerOrganizationIdFromModel(value: number | null): void {
    this.draftFilters.set({
      ...this.draftFilters(),
      partnerOrganizationId:
        typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null,
    });
  }

  private fetchPartners(): void {
    this.loadingPartners.set(true);

    this.http
      .get<PartnersResponse>(`${this.apiBaseUrl}/partners`, { withCredentials: true })
      .pipe(finalize(() => this.loadingPartners.set(false)))
      .subscribe({
        next: (response) => {
          this.partners.set(response.data ?? []);
        },
        error: () => {
          this.partners.set([]);
        },
      });
  }

  private fetchHomeFeed(): void {
    const query = this.filters();
    let params = new HttpParams().set('status', query.status);

    if (query.partnerOrganizationId) {
      params = params.set('partnerOrganizationId', String(query.partnerOrganizationId));
    }

    if (query.search.trim()) {
      params = params.set('search', query.search.trim());
    }

    if (query.toBeClosedOnly) {
      params = params.set('toBeClosedOnly', 'true');
    }

    this.loading.set(true);
    this.errorMessage.set(null);

    this.http
      .get<HomeFeedResponse>(`${this.apiBaseUrl}/cases/home-feed`, {
        params,
        withCredentials: true,
      })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response) => {
          this.groups.set(response.groups ?? []);
          this.totalClients.set(response.totalClients ?? 0);
          this.totalCases.set(response.totalCases ?? 0);
          this.stats.set(response.stats ?? null);
        },
        error: (error) => {
          this.errorMessage.set(
            error?.error?.message ?? 'Unable to load homepage data right now.',
          );
          this.groups.set([]);
          this.totalClients.set(0);
          this.totalCases.set(0);
          this.stats.set(null);
        },
      });
  }

  private defaultFilters(
    role: 'admin' | 'dekalb' | 'partner',
    partnerOrganizationId: number | null,
  ): HomeFilterState {
    return {
      status: 'open',
      partnerOrganizationId: role === 'partner' ? partnerOrganizationId : null,
      search: '',
      toBeClosedOnly: false,
    };
  }

  private normalizeFilters(
    filters: HomeFilterState,
    role: 'admin' | 'dekalb' | 'partner',
    partnerOrganizationId: number | null,
  ): HomeFilterState {
    const normalizedPartnerOrganizationId =
      typeof filters.partnerOrganizationId === 'number'
      && Number.isInteger(filters.partnerOrganizationId)
      && filters.partnerOrganizationId > 0
        ? filters.partnerOrganizationId
        : null;

    const normalized: HomeFilterState = {
      status: filters.status,
      partnerOrganizationId: normalizedPartnerOrganizationId,
      search: filters.search.trim(),
      toBeClosedOnly: filters.toBeClosedOnly,
    };

    if (role === 'partner') {
      normalized.partnerOrganizationId = partnerOrganizationId;
      normalized.toBeClosedOnly = false;
    }

    if (normalized.toBeClosedOnly) {
      normalized.status = 'all';
    }

    return normalized;
  }

  private readFiltersFromCookie(
    role: 'admin' | 'dekalb' | 'partner',
    partnerOrganizationId: number | null,
  ): HomeFilterState {
    const defaults = this.defaultFilters(role, partnerOrganizationId);
    const raw = this.readCookie(HOME_FILTER_COOKIE_NAME);
    if (!raw) {
      return defaults;
    }

    try {
      const parsed = JSON.parse(raw) as Partial<HomeFilterState>;
      const merged: HomeFilterState = {
        status:
          parsed.status === 'open' || parsed.status === 'closed' || parsed.status === 'all'
            ? parsed.status
            : defaults.status,
        partnerOrganizationId:
          typeof parsed.partnerOrganizationId === 'number' ? parsed.partnerOrganizationId : defaults.partnerOrganizationId,
        search: typeof parsed.search === 'string' ? parsed.search : defaults.search,
        toBeClosedOnly: parsed.toBeClosedOnly === true,
      };
      return this.normalizeFilters(merged, role, partnerOrganizationId);
    } catch {
      return defaults;
    }
  }

  private writeFiltersCookie(filters: HomeFilterState): void {
    const value = encodeURIComponent(JSON.stringify(filters));
    const maxAgeSeconds = 60 * 60 * 24 * 180;
    document.cookie = `${HOME_FILTER_COOKIE_NAME}=${value}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax`;
  }

  private readCookie(name: string): string | null {
    const key = `${name}=`;
    const segments = document.cookie.split(';');
    for (const segment of segments) {
      const candidate = segment.trim();
      if (candidate.startsWith(key)) {
        return decodeURIComponent(candidate.slice(key.length));
      }
    }
    return null;
  }
}
